<template>
  <div>
    <menu-group class="itemheader">
      <h1 class="itemheader__title self-center">
        {{ $t("labels.createRelation") }}
      </h1>

      <button class="button-icon" type="button">
        <lucide-icon name="x" @click="closeMenu" />
      </button>
    </menu-group>

    <menu-group>
      <input-dropdown
        :options="allItems"
        :value="builderCtx.baseElement"
        prop="title"
        group-key="type"
        item-key="uid"
        :label="$t('labels.outgoingElement')"
        @update:value="selectBase"
      >
        <template #grouplabel="{ value: itemType }">
          <div class="options-list__grouplabel">
            {{ $t(`objectNames.${itemType}`, 3) }}
          </div>
        </template>
      </input-dropdown>
    </menu-group>
    <menu-group v-if="builderCtx.baseElement">
      <label class="label">{{ $t("labels.description") }}</label>
      <formatted-read-more-text
        v-if="builderCtx.baseElement.description.length > 0"
        background-color="var(--color-background-medium)"
        :value="builderCtx.baseElement.description"
        :max-height="120"
      />
      <p v-else class="message mt-0">
        {{ $t("errors.noDescription") }}
      </p>
    </menu-group>

    <template v-if="builderCtx.endElement != null">
      <item-header
        :type="builderCtx.endElement.type"
        class="outgoing-relations-menu__elementselect"
        :label="$t(`labels.incoming.${builderCtx.endElement.type}`)"
        :button-centered="true"
        @close="selectEnd(null)"
      >
        <template #content>
          <input-dropdown
            :options="otherElements"
            prop="title"
            :select="endElementIndex"
            @select="selectEnd($event.key)"
          />
        </template>
      </item-header>
      <menu-group>
        <label class="label">{{ $t("labels.description") }}</label>
        <formatted-read-more-text
          v-if="builderCtx.endElement.description.length > 0"
          :value="builderCtx.endElement.description"
          background-color="var(--color-background-medium)"
          :max-height="120"
        />
        <p v-else class="message mt-0">
          {{ $t("errors.noDescription") }}
        </p>
      </menu-group>
    </template>

    <menu-group v-if="builderCtx.endElement == null">
      <label class="label">{{ $t("labels.elementOverview") }}</label>
      <p v-if="otherElements.length == 0">
        {{ $t("errors.noElementsToConnect") }}
      </p>
      <div v-else class="list mb-4">
        <div
          v-for="item in otherElements"
          :key="item.id"
          class="outgoing-relations-menu__endelement"
        >
          <button-item
            @click="selectElement(item)"
            @pointerenter="highlightElement(item)"
            @pointerout="unHighlightElement(item)"
          >
            {{ item.title }}
          </button-item>
          <div class="outgoing-relations-menu__endelement__status">
            <span v-if="outgoingRelations[`${item.type}.${item.id}`] != null">
              <lucide-icon
                v-if="
                  outgoingRelations[`${item.type}.${item.id}`].updateType ==
                  'none'
                "
                name="x"
              />
              <template v-else>
                <lucide-icon
                  v-if="outgoingRelations[`${item.type}.${item.id}`].inverse"
                  name="inverse"
                />
                <lucide-icon
                  v-if="!outgoingRelations[`${item.type}.${item.id}`].inverse"
                  name="not-inverse"
                />
              </template>
            </span>
          </div>
        </div>
      </div>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { mapKeys } from "lodash-es";

import MenuGroup from "@core/ui/MenuGroup.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputDropdown from "@core/input/InputDropdown.vue";

import { useRelationBuilder } from "./relationBuilder.js";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "../resource/resources.store";

import { useFeatures } from "@editor/store/features.store";
import { useIndexStore } from "../index/indices.store";
import { useModelViewStore } from "@editor/views/model/modelView.store";

import type { ResourceItem } from "../resource/ResourceItem";
import type { Uid } from "@/helpers/utility/Id.js";

const features = useFeatures();
const indexStore = useIndexStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const modelViewStore = useModelViewStore();

const builderCtx = useRelationBuilder();

const allItems = computed(() => {
  const enabledItems = [];
  if (features.target) {
    enabledItems.push(...targetStore.orderedItems);
  }
  if (features.resource) {
    enabledItems.push(...resourceStore.orderedItems);
  }
  if (features.index) {
    enabledItems.push(...indexStore.orderedItems);
  }
  return enabledItems;
});

const otherElements = computed(() => {
  const resources: ResourceItem[] = resourceStore.orderedItems.filter(
    (res) =>
      res.id != builderCtx.baseElement?.id ||
      builderCtx.baseElement.type != "resource"
  );

  const targets = targetStore.orderedItems.filter(
    (el) =>
      el.id != builderCtx.baseElement?.id || builderCtx.baseElement.type != "target"
  );
  return [...targets, ...resources];
});

const endElementIndex = computed(() => {
  return otherElements.value.findIndex(
    (item) =>
      item.type === builderCtx.endElement?.type &&
      item.id === builderCtx.endElement.id
  );
});

const outgoingRelations = computed(() => {
  if (builderCtx.baseElement == null) return {};
  const baseItemUid = `${builderCtx.baseElement.type}_${builderCtx.baseElement.id}`;
  return mapKeys(
    relationStore.byStartUid[baseItemUid],
    (relation) => `${relation.endItemType}.${relation.endItemId}`
  );
});

function selectElement(element: { type: "target" | "resource"; id: number }) {
  selectionStore.clearHighlights();
  selectionStore.select(element);
}

function highlightElement(element: { type: "target" | "resource"; id: number }) {
  selectionStore.setHighlight(element);
}

function unHighlightElement(element: { type: "target" | "resource"; id: number }) {
  selectionStore.removeHighlight(element);
}

function selectBase(element: { type: "target" | "resource" | "index"; id: number; uid: Uid<"target" | "resource" | "index"> }) {
  const { id, type } = element;
  selectionStore.clearAll();
  selectionStore.select({ id: id, type: type });
  modelViewStore.impactSourceElement = { uid: element.uid };
}

function selectEnd(elementIndex: number | null) {
  if (elementIndex == null) {
    selectionStore.clear();
  } else {
    const item = otherElements.value[elementIndex];
    selectionStore.select(item);
  }
}

watch(() => builderCtx.baseElement, (baseItem) => {
  if (baseItem == null) {
    closeMenu();
  }
});

function closeMenu() {
  modelViewStore.impactSourceElement = null;
}
</script>

<style lang="scss">
.outgoing-relations-menu {
  &__elementselect {
    background-color: transparent;
  }
  &__endelement {
    display: flex;

    &__status {
      width: 4rem;
      flex-grow: false;
      text-align: right;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>
